import {Autocomplete, AutocompleteProps, TextField, TextFieldProps} from "@mui/material";
import {FocusEvent, useEffect, useRef, useState} from "react";
import {Control, Controller, FieldValues, Path, useFormContext} from "react-hook-form";
import {useRecoilState} from "recoil";
import {autoCompleteAtom} from "../../state/pricingRecoil";
import {debounce} from "underscore";

type FormAutocompleteProps<IFormValues extends FieldValues = FieldValues, T extends any = any, Multiple extends (boolean | undefined) = undefined, DisableClearable extends (boolean | undefined) = undefined, FreeSolo extends (boolean | undefined) = undefined> =
    Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>, "ref" | "value" | "renderInput">
    & {
    name: Path<IFormValues>,
    control?: Control<IFormValues, any>,
    label: string,
    handleChange?: Function,
    required?: boolean,
    helperText?: string,
    error?: boolean,
    textFieldProps?: Omit<TextFieldProps, "name" | "value" | "label" | "error">
    outOfControl?: boolean,
    value?: any
}

const FormAutocomplete = <IFormValues extends FieldValues, T extends any = any, Multiple extends boolean | undefined = boolean, DisableClearable extends boolean | undefined = boolean, FreeSolo extends boolean | undefined = boolean>(
    {
        name,
        control,
        label,
        handleChange,
        required,
        helperText,
        error,
        textFieldProps,
        outOfControl,
        value,
        ...props
    }: FormAutocompleteProps<any, T, Multiple, DisableClearable, FreeSolo>) => {

    // Use Form Context
    const formContext = useFormContext<IFormValues>();
    const [autoComplete, setAutoComplete] = useRecoilState(autoCompleteAtom);
    const [open, setOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const autoCompleteRef = useRef<any>(null);

    useEffect(() => {
        if (autoComplete?.find(p => p.name === name)?.status) {
          if (isFocused) {
            setOpen(true);
          } else {
            autoCompleteRef.current?.focus();
            setOpen(true);
          }
        } else {
            setOpen(false);
        }
    }, [autoComplete]);

    const setAutoCompleteDebounced = debounce(() => {
        if (props.multiple) {
            autoCompleteRef.current?.focus();
            setAutoComplete([...autoComplete, {name: name, status: true}]);
        }
    }, 100);

    if (outOfControl) {
        return (

            <Autocomplete
                {...props}
                value={value}
                onChange={(e, value, reason, details) => {
                    if (handleChange) {
                        handleChange(value);
                    } else {
                        props.onChange?.(e, value, reason, details);
                    }
                    setAutoCompleteDebounced();
                }}
                onBlur={(e: FocusEvent<HTMLDivElement, Element>) => {
                    props.onBlur?.(e);
                    setAutoComplete([...autoComplete.filter(p => p.name !== name)]);
                }}
                open={open}
                onFocus={async (e: FocusEvent<HTMLDivElement, Element>) => {
                    await setIsFocused(true);
                    setAutoComplete([...autoComplete, {name: name, status: true}]);
                }}
                disableCloseOnSelect={true}
                limitTags={10}
                renderInput={(params) =>
                    <TextField
                        {...textFieldProps}
                        {...params}
                        inputRef={autoCompleteRef}
                        label={label}
                        fullWidth
                        required={required}
                        error={error}
                        helperText={helperText}
                        FormHelperTextProps={{
                            component: "div"
                        } as any}
                    />
                }
            />
        );
    } else {
        return (
            <Controller
                name={name}
                control={control ?? formContext.control}
                render={({field, fieldState, formState}) =>
                    <Autocomplete
                        {...props}
                        ref={field.ref}
                        value={field.value}
                        onChange={(e, value, reason, details) => {
                            if (handleChange) {
                                handleChange(value);
                            } else {
                                field.onChange(value);
                                props.onChange?.(e, value, reason, details);
                            }
                            setAutoCompleteDebounced();
                        }}
                        onBlur={(e: FocusEvent<HTMLDivElement, Element>) => {
                            field.onBlur();
                            props.onBlur?.(e);
                            setAutoComplete([...autoComplete.filter(p => p.name !== name)]);
                        }}
                        open={open}
                        onFocus={async (e: FocusEvent<HTMLDivElement, Element>) => {
                          await setIsFocused(true);
                          setAutoComplete([...autoComplete, {name: name, status: true}]);
                        }}
                        disableCloseOnSelect={true}
                        limitTags={10}
                        renderInput={(params) =>
                            <TextField
                                {...textFieldProps}
                                {...params}
                                inputRef={autoCompleteRef}
                                disabled={props.readOnly ?? formState.isSubmitting}
                                label={label}
                                error={!!fieldState.error}
                                fullWidth
                                required={required}
                                FormHelperTextProps={{
                                    component: "div"
                                } as any}
                                helperText={helperText ?? fieldState.error?.message ?? textFieldProps?.helperText}
                            />
                        }
                    />
                }
            />
        );
    }
};

export default FormAutocomplete;
